import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { ga4 } from "../analytics/ga4";
import { onConsentGranted, resetTracker } from "../analytics/tracker";
import { COOKIE_POLICY_URL } from "../config/legalLinks";

/**
 * Cookie consent banner nativo (multilingua via i18n: it/en/de).
 *
 * Sostituisce il banner iubenda (che nel nostro account era configurato solo in
 * tedesco). Unico flusso di tracciamento: app → dataLayer → GTM → GA4.
 *
 * Il Consent Mode v2 è dichiarato in `index.html` con tutto negato di default e
 * il container GTM NON viene caricato finché non c'è il consenso: qui si limita
 * ad aggiornare il consenso e ad avviare il container (`window.gbLoadGTM`).
 * La scelta è modificabile dal link "Cookie" nel footer (evento gb:open-cookie).
 */

const CONSENT_KEY = "gb_cookie_consent"; // "granted" | "denied"

type ConsentWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  gbLoadGTM?: () => void;
  __gbGtmLoaded?: boolean;
};

/** Aggiorna il Consent Mode. Solo `analytics_storage`: non esiste una categoria
 *  marketing separata nel banner, quindi i consensi pubblicitari restano negati. */
function updateConsent(granted: boolean): void {
  try {
    (window as ConsentWindow).gtag?.("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  } catch {
    /* il consenso non deve mai rompere la pagina */
  }
}

/** Rimuove i cookie _ga* dopo una revoca (su dominio corrente e dominio padre). */
function clearAnalyticsCookies(): void {
  try {
    const host = location.hostname;
    const domains = [host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (!/^_ga/.test(name)) return;
      domains.forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
      });
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch {
    /* noop */
  }
}

export function CookieConsent() {
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  // Visibile dal primo render se non c'è ancora una scelta salvata. Se lo
  // storage non è leggibile la scelta resta sconosciuta e il banner si MOSTRA,
  // come prima: nel dubbio si chiede. "granted" è già stato gestito in
  // index.html (consent update + gbLoadGTM) prima del mount di React, per non
  // ritardare il primo page_view.
  const [visible, setVisible] = useState(() => {
    try {
      return localStorage.getItem(CONSENT_KEY) == null;
    } catch {
      return true; // storage non disponibile
    }
  });

  // Altezza del banner (più la distanza dal fondo) come variabile CSS su <html>:
  // il pannello «Inviaci una richiesta» la usa per non finire sotto il banner
  // alla prima visita. Senza ResizeObserver resta il valore misurato all'apertura.
  const bannerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!visible || !el) {
      root.style.removeProperty("--gb-cookie-h");
      return;
    }
    const publish = () => {
      root.style.setProperty("--gb-cookie-h", `${Math.ceil(el.getBoundingClientRect().height) + (isMobile ? 12 : 24)}px`);
    };
    publish();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--gb-cookie-h");
    };
  }, [visible, isMobile]);

  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener("gb:open-cookie", reopen);
    return () => window.removeEventListener("gb:open-cookie", reopen);
  }, []);

  function choose(consent: "granted" | "denied") {
    const w = window as ConsentWindow;
    const wasTracking = w.__gbGtmLoaded === true;
    try {
      localStorage.setItem(CONSENT_KEY, consent);
    } catch {
      /* ignore */
    }
    updateConsent(consent === "granted");

    if (consent === "granted") {
      w.gbLoadGTM?.(); // primo avvio del container: prima di qui nessun hit analytics
      ga4.flushAfterConsent(); // recupera il view_item della scheda prodotto aperta
      onConsentGranted();      // recupera la page_view della pagina d'ingresso
      setVisible(false);
      return;
    }

    // Revoca: il container è già in pagina e continuerebbe a valutare i tag.
    // Ripulisco i cookie analytics e ricarico, così GTM non viene più caricato.
    clearAnalyticsCookies();
    // Anche il collector proprietario va azzerato: coda svuotata e identificatori
    // cancellati, così al prossimo consenso la persona riparte come sconosciuta.
    resetTracker();
    setVisible(false);
    if (wasTracking) window.location.reload();
  }

  if (!visible) return null;

  const btnBase: React.CSSProperties = {
    border: "none",
    borderRadius: 8,
    padding: "11px 22px",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.3,
    cursor: "pointer",
    transition: "opacity 0.2s",
    whiteSpace: "nowrap",
  };

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label={t("a11y.cookieBanner")}
      style={{
        position: "fixed",
        left: isMobile ? 12 : 24,
        right: isMobile ? 12 : 24,
        bottom: isMobile ? 12 : 24,
        zIndex: 130,
        background: cfg.color_body_bg,
        border: "1px solid rgba(28,75,59,0.89)",
        borderRadius: 14,
        boxShadow: "0 12px 48px rgba(0,0,0,0.16)",
        padding: isMobile ? "18px 18px 16px" : "20px 24px",
        maxWidth: 760,
        margin: "0 auto",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 14 : 20,
        fontFamily: cfg.font_body,
      }}
    >
      <p
        style={{
          margin: 0,
          flex: 1,
          fontSize: 13,
          lineHeight: 1.55,
          color: cfg.color_dark,
        }}
      >
        {t("cookie.message")}{" "}
        <a
          href={COOKIE_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: cfg.color_dark, textDecoration: "underline", fontWeight: 600 }}
        >
          {t("cookie.policyLink")}
        </a>
      </p>

      <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
        <button
          onClick={() => choose("denied")}
          style={{
            ...btnBase,
            background: "transparent",
            color: cfg.color_dark,
            border: "1px solid rgba(28,75,59,0.35)",
            flex: isMobile ? 1 : undefined,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
        >
          {t("cookie.reject")}
        </button>
        <button
          onClick={() => choose("granted")}
          style={{
            ...btnBase,
            background: cfg.color_dark,
            color: "#fff",
            flex: isMobile ? 1 : undefined,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
        >
          {t("cookie.accept")}
        </button>
      </div>
    </div>
  );
}
